/**
 * The console's drawer PANELS, registered against the shared shell (#543).
 *
 * The shell — rail, open-tenant persistence, scope bar, width publishing, empty
 * states — moved to `@solum/ui` so the portal has the same drawer rather than a
 * second one that drifts. What stayed here is everything that could not travel:
 * these eight panels read the console's `data/queries` and `crm/experience`
 * hooks, two console features (SLA insight, quick create), and the router's
 * `useNavigate` — and `apps/portal` has no router at all.
 *
 * The dispatch that used to live here was a nested conditional naming every
 * tenant, so a new panel meant another branch in a chain that already read as a
 * puzzle, plus an entry in an `EMPTY` map that had to exclude two tenants BY
 * NAME. It is a registry now: a panel declares whether it is standalone, whether
 * it is scoped, and what it says with no record on screen.
 *
 * `permitted={null}` — the console has no entitlement model for panels, because
 * everyone who can open a record here is staff. That is NOT an empty set, and
 * the shell keeps them distinct: the portal passes the persona's permitted keys.
 */
import { useMemo, useRef, useState, type ReactNode, useEffect } from 'react'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { recordDestination } from './recordDestination'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Row } from '@solum/sdk'
import { combineElevateFeeds, connectionTotal, type ElevateAction, type ElevateItem, type ElevateSourceFeed } from '@solum/experience'
import { Activity, ChartColumn, ClipboardCheck, Copy, Inbox, NotebookPen, ScanSearch, ShieldCheck, Sparkles, Waypoints } from 'lucide-react'
import { RecentPinnedPanel } from './RecentPinnedPanel'
import {
  adaptApprovalWorkContext, Button, cx, DataGrid, EntityIcon, Field, Pill, DrawerShell, isApprovalWorkAction, Select, WorkContext,
  ElevateCardList, ElevateTimelineToolbar, filterElevateTimeline, DEFAULT_ELEVATE_TIMELINE_VIEW,
  type ApprovalWorkAction, type ApprovalWorkContextData, type ElevateTimelineView,
  type DrawerPanel, type DockCount, DrawerRecordHead, Modal, EmptyState, ErrorState, FactList, Notice, SkeletonText, Connections as Connections_,
} from '@solum/ui'
import { ConsoleLauncher } from './ConsoleLauncher'
import { useSolum } from '../data/client'
import { useLiveInstalledModules } from '../data/moduleControl'
import {
  useEntityRow, useEntityPage, useEntityCount, useLookupLabels, useMyWork, useRecordIdentities, useRelated, useSaveEntity,
  reconcileRelationships,
} from '../data/queries'
import { useConnections } from '../record/useConnections'
import { recordHistoryKey, useMyToolInvocations, useRecordHistory } from '../audit/auditQueries'
import { DrawerGrid, type DrawerColumn, type DrawerRow } from './DrawerRows'
import {
  fmtMoney, fmtDate, me, useOwners, usePersona, usePersonas,
} from '../experience/governed'
import { recordRecent } from './recents'
import { useDrawerCtx, setDrawerCtx, type DrawerScope } from '@solum/ui'
import { SlaInsight } from '../service/SlaInsight'
import { QuickCreatePanel } from '../record/QuickCreatePanel'
import { onQuickCreateOpen } from '../record/quickCreate'
import { MeetingCapture } from '../engagements/MeetingCapture'
import { WorkQueuesPanel } from './WorkQueuesPanel'
import { elevateTimelineGroups, workEffectiveAt, type TimelineEntry } from './elevateTimeline'
import { openActivityComposer } from './ActivityComposerHost'
import s from './ShellDrawer.module.css'
import { CopilotConversation } from '../copilot/CopilotConversation'
import { readableError } from '@solum/sdk'


/**
 * Per-ENTITY insight cards (#327 / #337). The lens's general case is a CRM book;
 * for a record whose urgency is not a pipeline figure that block says nothing
 * about the thing on screen. A module registers what "insight" means for its own
 * record here, so the drawer dispatches instead of accumulating `if (entity …)`.
 */
const RECORD_INSIGHT: Record<string, (p: { id: string }) => ReactNode> = {
  service_case: SlaInsight,
}


/** The dock's marks are 16px stroke icons, the leading design's mark size. */
const MARK = { size: 16, strokeWidth: 1.8, 'aria-hidden': true, focusable: 'false' } as const

/**
 * The page's own Connections card, when the page carries one (#2406 dock).
 *
 * Scoped to `#main` and to the `workspace` presentation, which is the record
 * page's copy (`RelationCascade`) — never the drawer's own `panel` copy. The
 * first section is enough: it is the one the card's heading and count sit on.
 */
function connectionsOnPage(): HTMLElement | null {
  return document.querySelector<HTMLElement>('#main [data-testid="connections"][data-presentation="workspace"]')
}

/**
 * The Connections mark's count: the SAME governed total the lens and the page
 * card both state — `connectionTotal` over the capped `core.related_counts`
 * door — never a loaded-row length. A floor is said as one ("at least"), and a
 * total nothing could count draws nothing rather than a zero.
 */
function useConnectionsCount(entity: string, id: string): DockCount | null {
  const { groups } = useConnections(entity, id)
  return useMemo(() => {
    const total = connectionTotal(groups)
    if (total.n <= 0) return null
    const floor = total.atLeast || total.unknown
    return { n: total.n, atLeast: floor, label: `${floor ? 'At least ' : ''}${total.n} connected` }
  }, [groups])
}

/**
 * The drawer head's identity for the bound record (#2406 dock, `.rdh`): the
 * governed name and kind through the one identity door every surface reads,
 * and the way to the full record — offered only when this is not already it.
 */
function DrawerIdentity({ entity, id }: { entity: string; id: string }) {
  const navigate = useNavigate()
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const refs = useMemo(() => [{ type: entity, id }], [entity, id])
  const who = useRecordIdentities(refs).identity(entity, id)
  const destination = recordDestination(entity, id)
  const here = destination.to === '/crm' ? pathname === '/crm' : pathname === `/e/${entity}/${id}`
  return (
    <DrawerRecordHead
      mark={<EntityIcon iconKey={who.iconKey} size={16} />}
      title={who.title}
      sub={[who.typeLabel, who.subtitle].filter(Boolean).join(' · ')}
      action={here ? undefined : (
        <Button size="sm" variant="secondary" data-testid="drawer-open-record"
          onClick={() => { recordRecent(entity, id); void navigate(destination) }}>
          Open full record
        </Button>
      )} />
  )
}

export function ShellDrawer() {
  const persona = usePersona()
  const personas = usePersonas()
  const navigate = useNavigate()
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const contextWorkspaceActive = /^\/context\/[^/]+$/.test(pathname)

  /* The persona's default scope (0030 data: sales defaults to the record,
   * delivery/finance to their book). The shell takes it rather than reading it,
   * because "what a persona is" is a console concept and the portal has its own. */
  const defaultScope =
    (((personas.data ?? []) as Row[]).find((p) => p.key === persona)?.default_scope as DrawerScope | undefined) ?? 'mine'

  const panels = useMemo<DrawerPanel[]>(() => [
    /* ONE SURFACE, ONE TRIGGER (#2328 ROOT ruling 74071 §8, granted as a file
       handoff in mailbox #1863 comment 74155). The panel, its registration, its
       content and its persisted open state are #2325's and are untouched: this
       is still the lens `requestDrawerPanel('nav')` opens, still standalone,
       still closing on Escape, still named the same two ways.
       What `hostTriggered` removes is the drawer's own AFFORDANCE for it — the
       rail button, the sheet tab and the compact launcher's first-use default —
       because the console header now carries its one trigger
       (`RecentPinnedTrigger.tsx`). Deleting the entry outright would have taken
       the panel with it and left the header opening nothing. */
    { key: 'nav', glyph: <EntityIcon iconKey="history" />, label: 'Recently opened',
      actionName: (expanded) => `${expanded ? 'Close' : 'Open'} recently opened records`,
      compactLabel: 'Recently opened', closeOnEscape: true, hostTriggered: true,
      standalone: true, render: ({ closePanel }) => <RecentPinnedPanel closePanel={closePanel} /> },
    /* A queue is a pool, not an assignee or an authority shortcut. This lens is
       deliberately separate from My work: queued items belong to nobody until
       the existing atomic pickup verb moves one to the caller's person. */
    { key: 'queues', glyph: <Inbox {...MARK} />, label: 'Work queues — ready to pick up',
      standalone: true, render: () => <WorkQueuesPanel /> },
    { key: 'work', glyph: <ClipboardCheck {...MARK} />, label: 'Decision workspace',
      empty: 'Open a decision — its context, evidence and legal actions dock here.',
      render: ({ entity, id }) => <ApprovalWorkPanel entity={entity} id={id} /> },
    { key: 'context', glyph: <ScanSearch {...MARK} />, label: 'Marble Context — investigate this record',
      empty: 'Open a record — its governed investigations dock here.',
      render: ({ entity, id, openWorkspace }) => <ContextLens entity={entity} id={id} onOpenWorkspace={openWorkspace} /> },
    /* On a canonical record the page ALREADY carries the Connections card, so
       the mark rings it rather than opening a second copy (#2406 dock — the
       drawer's own copy was the "two panels on this page" `accounts.spec.ts`
       had to disambiguate by presentation). Its count is the one governed
       total the lens itself states. */
    { key: 'connections', glyph: <Waypoints {...MARK} />, label: 'Connections',
      onPage: connectionsOnPage, useCount: useConnectionsCount,
      empty: 'Open a record — everything it is connected to docks here.',
      render: ({ entity, id }) => <Connections entity={entity} id={id} /> },
    { key: 'timeline', glyph: <Activity {...MARK} />, label: 'Timeline', scoped: true,
      empty: 'Open a record — its activity timeline docks here.',
      render: ({ entity, id, scope }) => <Timeline entity={entity} id={id} scope={scope} /> },
    { key: 'insights', glyph: <ChartColumn {...MARK} />, label: 'Insights', scoped: true,
      empty: 'Open a record — its live insights dock here.',
      render: ({ entity, id, scope }) => <Insights entity={entity} id={id} scope={scope} /> },
    { key: 'access', glyph: <ShieldCheck {...MARK} />, label: 'Ownership & access',
      empty: 'Open a record — who owns and can access it docks here.',
      render: ({ entity, id }) => <Access entity={entity} id={id} /> },
    /* The '＋' Quick-create lens is GONE (Christopher, 2026-08-06: "I want the
       '+' module removed as well. It can be launched from palette anyway, so
       not relevant"). The drawer's job is contextual and overlapping
       information — insights, access, timeline, duplicates, and the navigation
       collection — never a form and never a create. Create lives in ⌘K, scoped
       to the grid you invoked it from, commands first with create at the top.
       The panel itself still MOUNTS: `openQuickCreate` is how a relation band
       creates a linked record, so the seam stays and only the lens goes. */
    { key: 'duplicates', glyph: <Copy {...MARK} />, label: 'Possible duplicates',
      empty: 'Open a person or an organization — possible duplicates dock here.',
      render: ({ entity, id, openWorkspace }) => <Duplicates entity={entity} id={id} onOpenWorkspace={openWorkspace} /> },
    // STANDALONE, like nav and create: this is a lens on YOU, not on the record
    // on screen. Scoping it to a record would answer a question nobody asks
    // ("which tools were run about this deal?") and hide the one they do.
    { key: 'copilot', glyph: <Sparkles {...MARK} />, label: 'Copilot',
      standalone: true, render: () => <CopilotConversation history={<CopilotHistory />} /> },
    /* STANDALONE, like nav and copilot: meeting capture is a lens on YOUR work,
       reachable from any screen (#998). Pick a meeting, log a decision/action/
       open point to it through the governed engagements verbs — the deterministic
       path works with no copilot, which is the mbn demo. */
    { key: 'capture', glyph: <NotebookPen {...MARK} />, label: 'Capture to a meeting',
      standalone: true, render: () => <MeetingCapture /> },
  ], [])

  return (<>
    <DrawerShell
      panels={panels}
      permitted={null}
      defaultScope={defaultScope}
      workspaceActive={contextWorkspaceActive}
      workspaceLabel="Marble Context workspace"
      onOpenWorkspace={(sessionId) => void navigate({ to: '/context/$id', params: { id: sessionId } })}
      onCloseWorkspace={() => window.history.back()}
      /* The wall's cap IS the product launcher (#2406): its one mount. */
      cap={<ConsoleLauncher />}
      renderHead={({ entity, id }) => <DrawerIdentity entity={entity} id={id} />}
    />
    {/* Quick-create is a MODAL, not a drawer lens. Removing the '＋' lens without
        this would have orphaned `openQuickCreate` — the relation band's create
        verb calls it, and a create affordance deleted rather than moved is the
        mistake `config.onNew` already taught (2026-08-07). The drawer stays
        contextual; the form gets a dialog. */}
    <QuickCreateHost />
  </>)
}

/**
 * The compact F-35 Context presentation. It is intentionally a lens on the
 * record already bound by the shell: no copied form, no source-table query and
 * no AI shortcut. Opening the workspace uses the canonical shell route; starting
 * one delegates to the governed record door, which derives the account floor.
 */
function ContextLens({ entity, id, onOpenWorkspace }: {
  entity: string
  id: string
  onOpenWorkspace?: (sessionId: string) => void
}) {
  const { client, isLive } = useSolum()
  const qc = useQueryClient()
  const refs = useMemo(() => [{ type: entity, id }], [entity, id])
  const records = useRecordIdentities(refs)
  const naming = records.identity(entity, id)
  const recordTitle = naming.title
  /* The title is WRITTEN into the new context (p_record_title), so it must be a
     governed answer — never the "Loading record…" or "Record unavailable"
     placeholder a pending or failed read shows (#2757 R2). */
  const titleSettled = naming.state === 'named' || naming.state === 'unnamed'
  const contexts = useQuery({
    queryKey: ['record-contexts', entity, id],
    enabled: isLive && !!entity && !!id,
    retry: false,
    queryFn: () => client.raw().rpcTable<Row>('contexts_for_record', {
      p_entity_type: entity, p_record_id: id, p_limit: 20,
    }, 'investigations'),
  })
  const rows = (contexts.data ?? []) as Row[]
  const latest = useQuery({
    queryKey: ['context-lens-detail', rows[0]?.id],
    enabled: isLive && Boolean(rows[0]?.id), retry: false,
    queryFn: () => client.raw().rpcScalar<Row | null>('hydrate_session', { p_session: String(rows[0]!.id) }, 'investigations'),
  })
  const start = useMutation({
    mutationFn: () => client.raw().rpcScalar<string>('start_from_record', {
      p_entity_type: entity, p_record_id: id, p_record_title: recordTitle,
      p_title: null, p_objective: null,
    }, 'investigations'),
    onSuccess: (sessionId) => {
      void qc.invalidateQueries({ queryKey: ['record-contexts', entity, id] })
      onOpenWorkspace?.(sessionId)
    },
  })
  const latestItems = ((latest.data?.items ?? []) as Row[])
  const latestSteps = ((latest.data?.plan_steps ?? []) as Row[])
  const blocker = latestItems.find((item) => ['stale', 'omitted', 'deleted'].includes(String(item.state)))
    ?? latestSteps.find((step) => ['stale', 'refused', 'failed'].includes(String(step.status)))
  const finding = [...latestItems].reverse().find((item) => String(item.kind) === 'decision' || String(item.origin) === 'ai')
  const next = latestSteps.find((step) => ['draft', 'ready', 'handed_off'].includes(String(step.status)))
  return (
    <div className={s.insights} data-testid="context-lens" style={{ gap: 'var(--sp-3)' }}>
      <div>
        <div className={s.sectionLabel}>Marble Context</div>
        <div style={{ color: 'var(--ink)', fontWeight: 680, marginTop: 4 }}>{recordTitle}</div>
        <div className="supporting" style={{ marginTop: 5 }}>
          Assemble permitted evidence, reason with Copilot, then hand reviewed work to its owning engine.
        </div>
      </div>
      {contexts.isPending && isLive && <SkeletonText lines={2} />}
      {contexts.isError && <EmptyState>Context is not installed here, or this record is outside your reach.</EmptyState>}
      {rows.length > 0 && (
        <div data-testid="context-grid">
          <DataGrid compact="cards" itemsLabel="contexts" rows={rows}
            columns={[{
              key: 'title', label: 'Context', width: 'minmax(0, 1fr)', compact: 'primary',
              cell: (row) => (
                <span style={{ minWidth: 0, width: '100%', display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
                  <span style={{ minWidth: 0, flex: 1, textAlign: 'start' }}>
                    <strong style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>{String(row.title ?? 'Untitled Context')}</strong>
                    {/* `DrawerGrid` has no cell-emphasis channel — its `DrawerColumn`
                        carries key/label/width/align and nothing about text. So this
                        takes the `supporting` utility directly rather than waiting for
                        one; `DataGrid`'s `emphasis` is the equivalent seam there. */}
                    <small className="supporting">{Number(row.item_count ?? 0)} blocks · {String(row.status ?? 'active')}</small>
                  </span>
                  <span aria-hidden>→</span>
                </span>
              ),
            }]}
            rowName={(row) => `${String(row.title ?? 'Untitled Context')}, ${Number(row.item_count ?? 0)} blocks, ${String(row.status ?? 'active')}`}
            onActivateRow={(contextId) => onOpenWorkspace?.(contextId)} />
        </div>
      )}
      {/* `group`: three related facts read as one region. Without a role the
          name is dropped (ARIA 1.2, #2151). */}
      {rows[0] && latest.data && <FactList framed label="Latest Context status" facts={[
        { key: 'blocker', label: 'Blocker', value: blocker ? String(blocker.title ?? blocker.action_key ?? blocker.status) : 'No blocker recorded' },
        { key: 'finding', label: 'Last finding', value: finding ? String(finding.title ?? 'Finding recorded') : 'No finding yet' },
        { key: 'next', label: 'Next action', value: next ? String(next.action_key ?? next.status).replaceAll('_', ' ') : 'Continue the investigation' },
      ]} />}
      {isLive && !contexts.isPending && !contexts.isError && rows.length === 0 && (
        <EmptyState>No investigation includes this record yet.</EmptyState>
      )}
      <Button size="sm" variant={rows.length ? 'secondary' : 'primary'} loading={start.isPending}
        disabled={!isLive || !onOpenWorkspace || !titleSettled || contexts.isError}
        onClick={() => start.mutate()}>
        Start from this record
      </Button>
      {rows[0] && <Button size="sm" variant="ghost" disabled={!onOpenWorkspace} onClick={() => onOpenWorkspace?.(String(rows[0]!.id))}>Ask in Context</Button>}
      {start.isError && <ErrorState title="That investigation could not be started" message={readableError(start.error)} />}
    </div>
  )
}

function ApprovalWorkPanel({ entity, id }: { entity: string; id: string }) {
  const { client } = useSolum()
  const raw = client.raw()
  const qc = useQueryClient()
  const q = useQuery({
    queryKey: ['decision-context', id, navigator.language],
    enabled: entity === 'approvals_instance' && !!id,
    queryFn: () => raw.rpcScalar<ApprovalWorkContextData>('my_decision_context',
      { p_instance_id: id, p_locale: navigator.language }, 'approvals'),
  })
  const action = useMutation({
    mutationFn: ({ verdict, reason }: { verdict: ApprovalWorkAction; reason?: string }) =>
      raw.rpcScalar<string>('act', { p_instance_id: id, p_verdict: verdict, p_reason: reason ?? null }, 'approvals'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['decision-context', id] })
      void qc.invalidateQueries({ queryKey: ['approvals-inbox'] })
    },
  })
  if (entity !== 'approvals_instance') return <EmptyState>Open a decision to see its work context.</EmptyState>
  if (q.isPending) return <SkeletonText lines={3} />
  if (q.isError || !q.data) return <ErrorState title="The decision context could not be loaded" />
  return <WorkContext context={adaptApprovalWorkContext(q.data)} onAction={async (verdict, reason) => {
    if (!isApprovalWorkAction(verdict)) throw new Error('Unsupported decision action')
    await action.mutateAsync({ verdict, reason })
  }} />
}

function QuickCreateHost() {
  const [open, setOpen] = useState(false)
  useEffect(() => onQuickCreateOpen(() => setOpen(true)), [])
  if (!open) return null
  return (
    <Modal label="Quick create" width={560} testid="quick-create-modal" onClose={() => setOpen(false)}>
      <QuickCreatePanel />
    </Modal>
  )
}

/* ── tenant #0: NAVIGATION — pinned + recently-opened, cross-record ── */
/* ── tenant: RECENT AND PINNED ────────────────────────────────────────────
 *
 * The body moved to `RecentPinnedPanel.tsx` (#2350 §9). Nothing about the
 * REGISTRATION changed — same `nav` key, same `hostTriggered`, same
 * `standalone`, same `closeOnEscape`, same two accessible names — so the
 * topbar trigger opens the same lens it always did and no second surface
 * exists. What left is the 145 lines of reading logic, which had outgrown a
 * file whose job is to REGISTER panels: this one is now the only tenant with
 * its own search, grouping, filters and keyboard model, and keeping it here
 * would have made the registry a place where one panel lives and eight are
 * listed. ── */

/* ── tenant: CONNECTIONS — the record's typed edges, through the ONE shared
 *    Connections surface (#1466). The panel that used to live here is deleted,
 *    not wrapped: it grouped by `rel_type` with the direction thrown away, so an
 *    organisation's inbound `works_at` read as "Works at" instead of "Employs";
 *    it printed the storage key as the heading; its count was the number
 *    LOADED; and its rows were buttons that navigated for three hardcoded
 *    entity types and did nothing at all for any other. All four are gone with
 *    it. Activity edges still belong to the Timeline — the shared model drops
 *    them, so that is now one rule in one place rather than a client-side
 *    filter in each surface. ── */
function Connections({ entity, id }: { entity: string; id: string }) {
  const navigate = useNavigate()
  const { groups, arrangement, busy, problem } = useConnections(entity, id)
  return (
    <Connections_
      groups={groups} arrangement={arrangement} busy={busy} problem={problem}
      presentation="panel"
      onOpen={(item) => {
        // Full records use the canonical route; an opportunity remains a CRM
        // desk peek. `recordRecent` keeps the recents rail honest.
        recordRecent(item.entity, item.id)
        void navigate(recordDestination(item.entity, item.id))
      }}
      partial="Typed edges from the kernel graph. Meaning, never permission — access lives under Ownership & access."
      empty="No connections on the graph yet."
    />
  )
}

/* ── tenant: DUPLICATES — the MDM steward's judgement, as ACTIONABLE CARDS ──
 *
 * The Elevate-sidepane pattern applied to the thing it suits best: a card that
 * states a finding, shows its evidence, and carries the two actions that
 * dispose of it. Christopher, 2026-08-03: *"within the elevate pane, we would
 * get actionable cards, just like the original D365 elevate solution"* — so the
 * shape follows `Elevate/src/components/Connections/ConnectionCard.tsx`: title,
 * evidence line, a confidence badge, actions on the card itself.
 *
 * Both actions go through the governed RPCs, never a table write. `confirm_match`
 * writes the identity edge AND elects a golden record; `reject_match` records the
 * "different people" so nobody is asked twice. A direct update would leave a pair
 * marked confirmed with no link behind it (core 2026080303's write policy is
 * admin-only for exactly that reason).
 *
 * NOTHING IS DELETED by confirming. The link is an edge, and the card says so —
 * a reviewer who is not certain still has an undo. */
function Duplicates({ entity, id, onOpenWorkspace }: { entity: string; id: string; onOpenWorkspace?: (sessionId: string) => void }) {
  const { client } = useSolum()
  const qc = useQueryClient()
  const [busy, setBusy] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [account, setAccount] = useState('')
  const accounts = useQuery({
    queryKey: ['mdm-context-accounts'],
    queryFn: () => client.raw().select<Row>('core', 'organization', { order: 'display_name.asc', limit: 100 }),
  })
  useEffect(() => {
    if (!account && accounts.data?.[0]?.id) setAccount(String(accounts.data[0].id))
  }, [account, accounts.data])

  const asA = useRelated('core', 'mdm_candidate', { entity_type: entity, a_id: id })
  const asB = useRelated('core', 'mdm_candidate', { entity_type: entity, b_id: id })
  const rows = [...((asA.data ?? []) as Row[]), ...((asB.data ?? []) as Row[])]
    .filter((r) => String(r.state) === 'open')
  const otherId = (r: Row) => (String(r.a_id) === id ? String(r.b_id) : String(r.a_id))
  const names = useLookupLabels(entity, rows.map(otherId))

  // Already-linked records: the identity edges this record sits on. Shown even
  // when there is nothing to decide, because "these two are the same person" is
  // the answer to a question people ask far more often than "is this a dupe?".
  const idOut = useRelated('core', 'relationship', { from_type: entity, from_id: id })
  const idIn = useRelated('core', 'relationship', { to_type: entity, to_id: id })
  const linked = [...((idOut.data ?? []) as Row[]).map((e) => ({ e, other: String(e.to_id) })),
                  ...((idIn.data ?? []) as Row[]).map((e) => ({ e, other: String(e.from_id) }))]
    .filter((x) => String(x.e.rel_type).endsWith('_same_as'))
  const linkedNames = useLookupLabels(entity, linked.map((x) => x.other))

  const decide = async (r: Row, verdict: 'confirm' | 'reject') => {
    setBusy(String(r.id)); setErr(null)
    try {
      await client.raw().rpcScalar(verdict === 'confirm' ? 'confirm_match' : 'reject_match', {
        p_entity_type: entity, p_a: String(r.a_id), p_b: String(r.b_id),
      }, 'core')
      await qc.invalidateQueries({ queryKey: ['related', 'core', 'mdm_candidate'] })
      /* `confirm_match` INSERTS an identity edge (`*_same_as`), so this is a
         relationship writer like any other and owes both records the same
         reconciliation a link does (#2000). The broad key it used before
         refreshed rows and no count at all, on either end. */
      await Promise.all([
        reconcileRelationships(qc, entity, String(r.a_id)),
        reconcileRelationships(qc, entity, String(r.b_id)),
      ])
    } catch (e) {
      // Named, not swallowed. The commonest refusal here is a real one — a
      // reviewer who owns neither record — and "nothing happened" would read as
      // a broken button.
      setErr(e instanceof Error ? e.message : String(e))
    } finally { setBusy(null) }
  }
  const review = async (candidateId: string) => {
    if (!account || !onOpenWorkspace) return
    setBusy(candidateId); setErr(null)
    try {
      const sessionId = await client.raw().rpcScalar<string>('start_mdm_review', {
        p_candidate: candidateId, p_account: account,
      }, 'investigations')
      onOpenWorkspace(sessionId)
    } catch (error) {
      setErr(error instanceof Error ? error.message : String(error))
    } finally { setBusy(null) }
  }

  const isParty = entity === 'core_person' || entity === 'core_organization'
  if (!isParty) {
    return <div className={s.insights} data-testid="duplicates">
      <EmptyState>Duplicate detection runs over people and organizations. This record is neither.</EmptyState>
    </div>
  }

  return (
    <div className={s.insights} data-testid="duplicates" style={{ gap: 'var(--sp-2)' }}>
      <div className={s.tlhead} style={{ padding: 0 }}>
        <span className={s.tlT}>Possible duplicates</span>
        <span className={s.tlSp} />
        <span className={cx('supporting', 'tnum')}>{rows.length} to review</span>
      </div>

      {err && <ErrorState title="That could not be recorded" message={err} data-testid="dup-error" />}

      {/* The muted class used to wrap this, which put `Field`'s own LABEL at
          --muted and --fs-sm — supporting-text treatment on a form label, which
          is an accessibility defect rather than a style. `Field` owns its
          typography; the wrapper owns nothing and is gone. */}
      {rows.length > 0 && <div>
        <Field label="Review within customer account">
        <Select value={account} onValueChange={setAccount} placeholder="Choose account…"
          options={(accounts.data ?? []).map((row) => ({ value: String(row.id), label: String(row.display_name ?? row.legal_name ?? 'Customer') }))} />
        </Field>
      </div>}

      {rows.map((r) => {
        const score = Number(r.score)
        return (
          <div key={String(r.id)} className={s.navRow} data-testid="dup-card"
               style={{ display: 'block', padding: 'var(--sp-3)' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--sp-2)' }}>
              <span className={s.navName} style={{ fontWeight: 600 }}>{names.label(otherId(r), String(otherId(r)))}</span>
              <span className={s.tlSp} />
              <Pill tone={score >= 1 ? 'good' : 'warn'}>{score >= 1 ? 'Certain' : `${Math.round(score * 100)}%`}</Pill>
            </div>
            {/* The match engine's rationale for THIS pair. Content, not
                chrome — the same call the returns desk's reason got. */}
            <div style={{ margin: '4px 0 8px' }}>{String(r.reason)}</div>
            <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
              <Button size="sm" variant="ghost" className={s.navMain} data-testid="dup-context" disabled={busy === String(r.id) || !account || !onOpenWorkspace}
                      style={{ padding: '4px 10px', border: '1px solid var(--brand)', borderRadius: 'var(--r-sm)' }}
                      onClick={() => void review(String(r.id))}>Review in Context</Button>
              <button className={s.navMain} data-testid="dup-confirm" disabled={busy === String(r.id)}
                      style={{ padding: '4px 10px', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)' }}
                      onClick={() => void decide(r, 'confirm')}>Same party</button>
              <button className={s.navMain} data-testid="dup-reject" disabled={busy === String(r.id)}
                      style={{ padding: '4px 10px', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)' }}
                      onClick={() => void decide(r, 'reject')}>Not the same</button>
            </div>
          </div>
        )
      })}

      {rows.length === 0 && <EmptyState>Nothing to review — no open duplicate candidates for this record.</EmptyState>}

      {linked.length > 0 && (
        <div>
          <div className={s.sectionLabel} style={{ margin: '8px 0 4px' }}>already linked as the same</div>
          {linked.map((x) => (
            <div key={String(x.e.id)} className={s.navRow}>
              <span className={s.navGlyph}>⧉</span>
              <span className={s.navName}>{linkedNames.label(x.other, x.other)}</span>
            </div>
          ))}
        </div>
      )}

      <div className="supporting">
        Confirming writes a link, and deletes nothing — the records stay, joined by an edge that can be
        removed. Rejecting is remembered, so the same pair is not raised again.
      </div>
    </div>
  )
}

/* ── tenant: COPILOT HISTORY — every tool call I have made, as rows ──────────
 *
 * Source: `core.tool_invocation` (core 2026080112, #396) — the governed
 * invocation log. One row per copilot or MCP tool call: the tool, the surface,
 * the outcome (ok / refused / error), how long it took. Structured signal only;
 * the migration deliberately records no prompts and no arguments, so there is
 * nothing here to redact and nothing here to leak.
 *
 * ── Why this panel needed a WRITE before it could be a panel ────────────────
 * Measured on a live box: the table had ZERO ROWS. The recording seam
 * (`@solum/sdk` `invocations.ts`) was called by the MCP connector and by the
 * copilot relay, and by nothing in the console — so the surface where an admin
 * actually runs verbs recorded nothing at all. A panel alone would have been
 * permanently empty and would have read as a broken feature rather than a
 * missing write. `copilot/tools.ts` now calls the same seam with
 * `surface: 'copilot'`; the CHECK constraint already admits it and no migration
 * was needed.
 *
 * ── Your own calls, through a governed projection (#2444 R2) ────────────────
 * The ledger is closed to every API role (core 2026092405), so the panel reads
 * `core.my_tool_invocations`: the CALLER's own rows — the server derives who
 * that is, and an admin gets their own calls, not the fleet's — on the
 * `(actor, invoked_at desc)` index. Structured fields only: the stored reason
 * is not a column of the projection (an error string can carry a value), and
 * neither are arguments, prompts or responses, which were never stored.
 *
 * ── Audience ───────────────────────────────────────────────────────────────
 * NOT `moonbase-internal`. A person's own record of what they asked the copilot
 * to do is a normal user feature — transparency about yourself, which is what
 * the migration's `private` visibility already encodes. Fleet-wide usage
 * analytics across every deployment would be a different surface and a different
 * audience decision; this is not that.
 */
const OUTCOME: Record<string, { glyph: string; tone?: 'warn' | 'crit'; label: string }> = {
  ok:      { glyph: '✓', label: 'OK' },
  refused: { glyph: '⊘', tone: 'warn', label: 'Refused' },
  error:   { glyph: '!', tone: 'crit', label: 'Error' },
}
const SURFACE_LABEL: Record<string, string> = { copilot: 'Copilot', mcp: 'MCP agent' }
const REFUSAL_OUTCOMES = ['refused', 'error'] as const

const HISTORY_COLUMNS: DrawerColumn[] = [
  { key: 'outcome', label: '', width: '22px' },
  { key: 'tool', label: 'Tool', width: 'minmax(0,1fr)' },
  { key: 'when', label: 'When', width: 'auto', align: 'right' },
]

/**
 * `invoked_at` is a timestamptz and these rows are minutes old, not months —
 * `fmtDate` would render every call today as the same string. Relative inside a
 * day, the date after that.
 */
function fmtWhen(iso: unknown): string {
  const t = Date.parse(String(iso ?? ''))
  if (Number.isNaN(t)) return '—'
  const secs = Math.max(0, Math.round((Date.now() - t) / 1000))
  if (secs < 60) return 'just now'
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`
  return fmtDate(iso)
}

function CopilotHistory() {
  // Mirrors the migration's two registered views ('Tool Invocations' and
  // 'Refusals & Errors') rather than inventing a third way to slice the same
  // table — the filter is pushed to the server so the toggle narrows the whole
  // log, not the page already fetched.
  const [refusalsOnly, setRefusalsOnly] = useState(false)
  const q = useMyToolInvocations(refusalsOnly ? REFUSAL_OUTCOMES : undefined)
  const rows = q.data?.rows ?? []

  const gridRows: DrawerRow[] = rows.map((r, i) => {
    const o = OUTCOME[r.outcome] ?? { glyph: '·', label: r.outcome }
    const tool = r.toolLabel ?? 'Unnamed tool'
    const bits = [
      r.surface ? SURFACE_LABEL[r.surface] ?? r.surface : null,
      r.module,
      r.durationMs != null ? `${r.durationMs} ms` : null,
    ].filter(Boolean)
    return {
      // The projection returns no id (none is needed to draw a row); the order
      // is the server's (newest first), so time + position keys it stably.
      key: `${r.at}:${i}`,
      label: `${tool} — ${o.label}`,
      tone: o.tone,
      cells: [
        // `img`: the cell is a GLYPH standing for the outcome, and its label is
        // the name of that image. A bare span cannot carry the name at all.
        <span key="o" role="img" title={o.label} aria-label={o.label}>{o.glyph}</span>,
        <span key="t" style={{ fontWeight: 600 }}>{tool}</span>,
        <span key="w" className="tnum">{fmtWhen(r.at)}</span>,
      ],
      detail: bits.join(' · '),
    }
  })

  return (
    <div className={s.insights} data-testid="copilot-history"
      style={{ gap: 'var(--sp-2)', padding: 0, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/* "You have made no tool calls" is a factual claim, and making it while
          the request is open — or after it failed — is the #385 defect one panel
          over. Say which it is. */}
      {q.isLoading
        ? <SkeletonText lines={4} />
        : q.isError
          ? <ErrorState title="Could not read the invocation log"
              message={(q.error as Error | null)?.message ?? 'unknown error'} />
          : <DrawerGrid columns={HISTORY_COLUMNS} rows={gridRows} testId="ch-grid"
              title="Copilot history"
              filters={(
                <span className={s.tlfilters} data-testid="ch-filters" data-active={refusalsOnly || undefined}>
                  <button aria-pressed={refusalsOnly} data-testid="ch-refusals"
                    onClick={() => setRefusalsOnly((v) => !v)}>⊘ refusals &amp; errors</button>
                </span>
              )}
              actions={<span className={cx('supporting', 'tnum')}>{q.data?.total ?? rows.length} calls</span>}
              empty={refusalsOnly
                ? 'Nothing refused — every tool call you have made went through.'
                : 'No tool calls yet. Ask the copilot (⌘J) or run a verb with /<name> — every call lands here.'} />}
      <div className="supporting" style={{ padding: 'var(--sp-3)' }}>
        Your own calls, from both surfaces — this console and any MCP agent signed in as you.
        The log keeps the tool, the outcome and the duration; never the prompt or the arguments.
      </div>
    </div>
  )
}

/* ── tenant #1: the timeline — contextual on the record, or MY WORK / the
 *    team's across the whole book (the Elevate viewMode model). Elevate
 *    parity: TYPE FILTER chips + AUDIT interleave (record scope only, like
 *    Elevate forces notes/audit off outside record context; in production the
 *    audit chip is privilege-gated — PANE_PRIVILEGES.timeline.audit). ── */
/**
 * How many activities one page of the record timeline draws.
 *
 * The door clamps its own ceiling at 200 regardless of what is asked; this is
 * the surface's choice within that, and it is deliberately smaller than the
 * ceiling so "there is more" is a normal answer rather than an edge case.
 */
const TIMELINE_PAGE = 50

/**
 * The door's own ceiling, restated so this surface stops asking at the same
 * point the door stops answering. It is not a second policy: exceeding it
 * would simply return the same rows again under a larger limit, and a
 * "Show more" that changes nothing is the worst kind of affordance.
 */
const TIMELINE_CEILING = 200

function Timeline({ entity, id, scope }: { entity: string; id: string; scope: DrawerScope }) {
  const ctx = useDrawerCtx()
  const ownerName = useOwners()
  const navigate = useNavigate()
  const { client, isLive } = useSolum()
  const installedModules = useLiveInstalledModules()
  const preferenceKey = `solum.timeline.view.${me()}`
  const [timelineView, setTimelineView] = useState<ElevateTimelineView>(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(preferenceKey) ?? 'null') as Partial<ElevateTimelineView> | null
      return stored ? { ...DEFAULT_ELEVATE_TIMELINE_VIEW, ...stored } : DEFAULT_ELEVATE_TIMELINE_VIEW
    } catch { return DEFAULT_ELEVATE_TIMELINE_VIEW }
  })
  useEffect(() => {
    try { localStorage.setItem(preferenceKey, JSON.stringify(timelineView)) } catch { /* private mode */ }
  }, [preferenceKey, timelineView])
  /* D365 Elevate treats change history as part of the record's story, not as a
   * second screen. Keep it interleaved by default; this control is an explicit
   * visibility filter for people who need a quieter activity-only view. */
  const [showAudit, setShowAudit] = useState(true)
  /* #1362: record scope is ONE governed set-form statement, not a raw activity
   * page plus one relationship query per person. The RPC returns the root and
   * its graph-linked person lanes already fenced to the root account. My/team
   * retain their existing whole-book table read. */
  /* #2024: the search, the filters and the page are the DOOR's work now. They
     used to happen in the browser, which meant every activity on the record
     crossed the wire before anything was narrowed — and the in-memory sort
     disagreed with the door's own ordering for undated rows, because the door
     ordered by a `created_at` it did not return.

     One row MORE than is drawn is requested, so the surface can say that more
     exists without a second count over the whole set. */
  const activityQuery = timelineView.query.trim()
  const activityKinds = timelineView.kinds.length > 0 ? [...timelineView.kinds] : null
  /* The ORDER goes to the door too (activity 0.14.0). Sorting in the browser
     could only ever reorder the page the door had already chosen, so "Oldest"
     answered with the oldest of the NEWEST fifty. The door refuses an order
     outside its allowlist by name, so this cannot silently degrade. */
  const activityOrder = timelineView.sort

  /* ── how many pages the person has asked for ──────────────────────────
     Reset whenever the QUESTION changes: a page 3 of one search is not page 3
     of the next, and carrying the offset across would skip rows silently. The
     identity below is exactly the identity in the query key, so the two
     cannot drift apart. */
  const timelineIdentity = [entity, id, ctx ?? "", activityQuery,
    activityKinds?.join(",") ?? "", timelineView.status, activityOrder].join("\u0000")
  const [pages, setPages] = useState(1)
  const identityRef = useRef(timelineIdentity)
  if (identityRef.current !== timelineIdentity) {
    identityRef.current = timelineIdentity
    if (pages !== 1) setPages(1)
  }

  const contextual = useQuery({
    queryKey: ['contextual-timeline', entity, id, ctx,
      activityQuery, activityKinds?.join(',') ?? '', timelineView.status,
      activityOrder, pages],
    enabled: scope === 'record',
    queryFn: () => client.raw().rpcTable<Row>('contextual_timeline', {
      p_root_type: entity, p_root_id: id, p_focus_id: ctx,
      p_query: activityQuery === '' ? null : activityQuery,
      p_kinds: activityKinds,
      p_status: timelineView.status === '' ? null : timelineView.status,
      p_order: activityOrder,
      /* Everything asked for so far, plus ONE probe row. The door caps at 200
         whatever is asked, so "more" is answered without a second count and
         the ceiling stays the door's rather than this surface's. */
      p_limit: Math.min(TIMELINE_PAGE * pages, TIMELINE_CEILING) + 1,
      p_offset: 0,
    }, 'activity'),
  })
  const drawnLimit = Math.min(TIMELINE_PAGE * pages, TIMELINE_CEILING)
  const pagedContextualRows = (contextual.data ?? []).slice(0, drawnLimit)
  /* A page that silently stops is a page that lies about the record. */
  const contextualHasMore = (contextual.data ?? []).length > drawnLimit
  /* ── the filter vocabulary, asked WITHOUT the current filter ───────────
     Deliberately keyed on the record alone: the whole point is that it does
     not move when a chip is pressed. Reading the chips off the filtered page
     is what let one selection erase the other choices. */
  const timelineFacets = useQuery({
    queryKey: ['contextual-timeline-facets', entity, id, ctx],
    enabled: scope === 'record',
    queryFn: () => client.raw().rpcTable<Row>('contextual_timeline_facets', {
      p_root_type: entity, p_root_id: id, p_focus_id: ctx,
    }, 'activity'),
  })
  const facetRows = (timelineFacets.data ?? []) as Row[]
  const elevateFacets = facetRows.length === 0 ? undefined : {
    kinds: facetRows.filter((r) => r.facet === 'channel')
      .map((r) => ({ value: String(r.value), n: Number(r.n) })),
    statuses: facetRows.filter((r) => r.facet === 'status')
      .map((r) => ({ value: String(r.value), n: Number(r.n) })),
  }

  const contextualTasks = useQuery({
    queryKey: ['contextual-tasks', entity, id],
    enabled: scope === 'record',
    queryFn: () => client.raw().rpcTable<Row>('contextual_tasks', {
      p_root_type: entity, p_root_id: id, p_limit: 100, p_offset: 0,
    }, 'activity'),
  })
  const meetingsInstalled = isLive && (installedModules.data ?? [])
    .some((module) => module.name === 'engagements-activity')
  const contextualMeetings = useQuery({
    queryKey: ['contextual-meeting-reports', entity, id],
    enabled: scope === 'record' && meetingsInstalled,
    queryFn: () => client.raw().rpcTable<Row>('contextual_meeting_reports', {
      p_root_type: entity, p_root_id: id, p_limit: 100, p_offset: 0,
    }, 'engagements'),
  })
  /* The team / mine lenses (#2444 slice 4). An activity is a SUBJECT entity:
     every row its door returns is a `record.viewed`. So the lens asks for what
     it DRAWS — the newest page, and for "mine" only the caller's own — rather
     than the whole book narrowed in the browser, which would receipt every
     colleague's activity to show none of them. `occurred_at` and `owner_id`
     are in the door's vocabulary (a registered field and the record kernel). */
  const acts = useRelated('activity', 'activity', scope === 'mine' ? { owner_id: me() } : {}, scope !== 'record',
    { order: 'occurred_at.desc' })
  /* The record's change history, through its governed projection
   * (`core.record_history`, #2444 R2). The evidence table itself is closed to
   * every API role; this answers labels and the server-derived detail only,
   * and nothing for a record the caller cannot see. */
  const audit = useRecordHistory(entity, id, scope === 'record')
  /* MY WORK is the work-surface aggregating read (core.my_work, invoker-under-RLS):
   * everything ASSIGNED to me across every module that declared its entity work —
   * not just the activities I own. This is the RFC §03 "your work" promotion of
   * this panel: same lens, one more (governed) source, no new panel. */
  const work = useMyWork(scope === 'mine')
  // ONE id, fetched by id — not a 500-row page to read one name off (TEC-02).
  const ctxOpp = useLookupLabels('crm_opportunity', scope === 'record' && ctx ? [ctx] : [])
  const recordIdentities = useRecordIdentities(scope === 'record' ? [{ type: entity, id }] : [])
  const recordTitle = scope === 'record' ? recordIdentities.identity(entity, id).title : ''
  // The page the door returned, less the probe row — see TIMELINE_PAGE.
  const contextualRows = pagedContextualRows as Row[]
  const ctxPerson = ctx ? contextualRows.find((r) => r.person_id === ctx)?.person_label : null
  const ctxName = scope === 'record' && ctx ? String(ctxPerson ?? ctxOpp.label(ctx, '')) : null

  let items: Row[] = (scope === 'record'
    ? contextualRows.map((r) => ({ ...r, id: r.activity_id }))
    : ((acts.data ?? []) as Row[]))
  if (scope === 'record') {
    // The RPC applies ctx inside the consolidated statement. Keeping a second
    // browser filter here would turn a related-record focus into an empty pane.
  } else if (scope === 'mine') {
    items = items.filter((a) => a.owner_id === me())
  }                                                      // team: the whole book
  /* assigned work rides the 'task' type chip — it IS the scheduled-judgement class */
  const workRows: Row[] = scope === 'record'
    ? ((contextualTasks.data ?? []) as Row[]).map((row): Row => ({ ...row, entity_type: 'core_task', module: 'core' }))
    : scope === 'mine' ? ((work.data ?? []) as Row[]) : []
  const meetingRows = scope === 'record' ? ((contextualMeetings.data ?? []) as Row[]) : []

  const rawEntries: TimelineEntry[] = [
    ...items.map((a) => ({ key: `a:${a.id}`, kind: 'activity' as const, at: String(a.occurred_at ?? a.due_at ?? ''), row: a })),
    ...workRows.map((w) => ({ key: `w:${String(w.entity_type)}:${String(w.id)}`, kind: 'work' as const, at: workEffectiveAt(w), row: w })),
    ...meetingRows.map((meeting) => ({ key: `m:${String(meeting.id)}`, kind: 'meeting' as const, at: String(meeting.scheduled_at ?? meeting.created_at ?? ''), row: meeting })),
    ...(showAudit && scope === 'record'
      ? (audit.data ?? []).map((a) => ({ key: `u:${a.seq ?? a.at}`, kind: 'audit' as const, at: a.at, row: { ...a } as Row }))
      : []),
  ].sort((a, b) => b.at.localeCompare(a.at))

  /* Group record history into attributable lanes before the shared Elevate
   * card renderer receives it. Audit belongs to the record lane; mine/team stay
   * flat because their organizing concept is assignment/scope. */
  const entries: TimelineEntry[] = scope !== 'record' ? rawEntries : (() => {
    const activityRows = rawEntries.filter((e) => e.kind === 'activity')
    const lanes = new Map<string, { label: string; rows: TimelineEntry[] }>()
    for (const e of activityRows) {
      const personId = String(e.row.person_id ?? '')
      const key = personId || 'record'
      const label = personId ? String(e.row.person_label ?? 'Linked person') : 'This record'
      const lane = lanes.get(key) ?? { label, rows: [] }
      lane.rows.push(e); lanes.set(key, lane)
    }
    const auditRows = rawEntries.filter((e) => e.kind === 'audit')
    const recordRows = rawEntries.filter((e) => e.kind === 'work' || e.kind === 'meeting')
    if (auditRows.length > 0 || recordRows.length > 0) {
      const lane = lanes.get('record') ?? { label: 'This record', rows: [] }
      lane.rows.push(...recordRows, ...auditRows); lane.rows.sort((a, b) => b.at.localeCompare(a.at)); lanes.set('record', lane)
    }
    return [...lanes.entries()]
      .sort(([ak, a], [bk, b]) => ak === 'record' ? -1 : bk === 'record' ? 1 : a.label.localeCompare(b.label))
      .flatMap(([key, lane]) => [
        { key: `lane:${key}`, kind: 'lane' as const, at: '', row: { person_id: key === 'record' ? null : key, label: lane.label, count: lane.rows.length } },
        ...lane.rows,
      ])
  })()
  const elevateGroups = elevateTimelineGroups(entries, ownerName)
  const allElevateItems = elevateGroups.flatMap((group) => group.items)
  const sourceFeed = (
    sourceId: string, sourceLabel: string, failed: boolean, error: unknown,
  ): ElevateSourceFeed => {
    const source = { id: sourceId, label: sourceLabel, kind: 'marble' as const, revision: 'live' }
    return failed
      ? { source, state: 'error', message: readableError(error, 'Source unavailable') }
      : { source, state: 'ready', items: allElevateItems.filter((item) => item.source.id === sourceId) }
  }
  const feeds: ElevateSourceFeed[] = scope === 'record'
    ? [
        sourceFeed('marble:activity', 'Marble Activity', contextual.isError, contextual.error),
        sourceFeed('marble:work', 'Marble Work', contextualTasks.isError, contextualTasks.error),
        ...(meetingsInstalled ? [sourceFeed('marble:meetings', 'Marble Meetings', contextualMeetings.isError, contextualMeetings.error)] : []),
        ...(showAudit ? [sourceFeed('marble:audit', 'Marble Changes', audit.isError, audit.error)] : []),
      ]
    : [
        sourceFeed('marble:activity', 'Marble Activity', acts.isError, acts.error),
        ...(scope === 'mine' ? [sourceFeed('marble:work', 'Marble Work', work.isError, work.error)] : []),
      ]
  const combined = combineElevateFeeds(feeds)
  const filteredItems = filterElevateTimeline(combined.items, timelineView)
  const visibleIds = new Set(filteredItems.map((item) => item.id))
  const contextGroups = elevateGroups.map((group) => ({
    id: group.id,
    label: group.label == null ? undefined : (
      <>
        {group.personId
          ? <button type="button" data-testid="tl-person-lane"
              aria-pressed={ctx === group.personId}
              onClick={() => setDrawerCtx(ctx === group.personId ? null : group.personId ?? null)}>
              {group.label}
            </button>
          : <span>{group.label}</span>}
        <span className="tnum">{group.items.length}</span>
      </>
    ),
    items: group.items.filter((item) => visibleIds.has(item.id)),
  })).filter((group) => group.items.length > 0)
  const groupLabel = (item: ElevateItem) => {
    if (timelineView.group === 'type') return String(item.channel ?? item.kind).replaceAll('_', ' ')
    if (timelineView.group === 'status') return item.status.label
    if (timelineView.group === 'owner') return item.actors.find((actor) => actor.role === 'owner' || actor.role === 'assignee')?.label ?? 'Unassigned'
    if (timelineView.group === 'date') {
      const date = new Date(item.time.effectiveAt)
      if (Number.isNaN(date.getTime())) return 'No date'
      const now = new Date()
      const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1)
      if (date.toDateString() === now.toDateString()) return 'Today'
      if (date.toDateString() === yesterday.toDateString()) return 'Yesterday'
      return new Intl.DateTimeFormat('en', { month: 'long', year: 'numeric' }).format(date)
    }
    return 'Timeline'
  }
  const cardGroups = timelineView.group === 'context' ? contextGroups : (() => {
    if (timelineView.group === 'none') return [{ id: 'timeline', items: filteredItems }]
    const grouped = new Map<string, ElevateItem[]>()
    for (const item of filteredItems) {
      const label = groupLabel(item)
      grouped.set(label, [...(grouped.get(label) ?? []), item])
    }
    return [...grouped.entries()].map(([label, groupedItems], index) => ({ id: `group-${index}`, label, items: groupedItems }))
  })()
  /* ADR F-29: what this host can execute is DECLARED, not inferred. An adapter
   * that starts offering a verb this renderer cannot route shows up as a
   * conformance defect on the card, not as a button that fails when pressed. */
  const hostActions = ['open'] as const
  const handleCardAction = (
    item: ElevateItem,
    action: Extract<ElevateAction, { state: 'available' }>,
  ) => {
    /* Unreachable while `supports` below names the same set — kept because an
     * assertion that cannot fire is cheaper than one that was never written. */
    if (action.id !== 'open') throw new Error(`The '${action.id}' action has no Marble host adapter.`)
    if (item.provenance.destination.state === 'refused') throw new Error(item.provenance.destination.reason)
    const go = navigate as unknown as (options: { to: string }) => void
    go({ to: item.provenance.destination.deepLink })
  }

  return (
    <aside className={s.tlpanel} style={{ position: 'static', maxHeight: 'none', flex: 1, minHeight: 0 }}
      aria-label="Activity timeline" data-testid="timeline">
      <div className={s.tlhead}>
        <span className={s.tlT}>{scope === 'mine' ? 'My work' : scope === 'team' ? 'Team activity' : 'Activity'}</span>
        {scope === 'record' && (ctxName
          ? <span className={s.ctxchip} data-testid="tl-ctx">◎ {ctxName}<button aria-label="Clear context" onClick={() => setDrawerCtx(null)}>×</button></span>
          : <span className="supporting">whole record</span>)}
        <span className={s.tlSp} />
        <span className={cx('supporting', 'tnum')}>{rawEntries.length} items</span>
      </div>
      <ElevateTimelineToolbar
        items={combined.items}
        facets={elevateFacets}
        value={timelineView}
        onChange={setTimelineView}
        sourceState={combined.state}
        onReset={() => setShowAudit(true)}
        data-testid="tl-add-activity"
        onCreate={scope === 'record' ? () => openActivityComposer({ context: { entity, id, label: recordTitle } }) : undefined}
      />
      {scope === 'record' && (
        <button type="button" className={s.tlaudit} aria-pressed={showAudit}
          data-testid="tl-audit-toggle" onClick={() => setShowAudit((value) => !value)}>
          {showAudit ? 'Changes included' : 'Changes hidden'}
        </button>
      )}
      {combined.unavailable.length > 0 && (
        <Notice tone="warn" live="status" data-testid="tl-unavailable-sources">
          Still showing available history. Unavailable: {combined.unavailable.map((source) => source.label).join(', ')}.
        </Notice>
      )}
      {/* ADR F-29: one Elevate card meaning across hosts. The adapter owns the
          Marble row mapping; this renderer owns progressive disclosure and
          keyboard focus; the source module still owns every available act. */}
      {scope === 'record' && contextual.isLoading && allElevateItems.length === 0
        ? <SkeletonText lines={4} />
        : <ElevateCardList
              groups={cardGroups}
              testId="tl-cards"
              query={timelineView.query}
              supports={hostActions}
              onAction={handleCardAction}
              empty={ctxName ? `Quiet on ${ctxName} — the panel scopes to what you click.`
                : filteredItems.length === 0 && allElevateItems.length > 0 ? 'Nothing matches these timeline filters.' : 'Quiet here.'}
            />}
      {/* A bounded page that does not say so is a page that misreports the
          record. The door caps at 200 whatever is asked; this draws 50 and asks
          for 51, so "there is more" is known without counting the set. */}
      {/* The muted class wrapped two paragraphs AND a Button, so the control
          inherited supporting typography. It is on the prose alone now. */}
      {scope === 'record' && contextualHasMore && (
        <div data-testid="tl-more" style={{ padding: 'var(--sp-3)' }}>
          <p role="status" className="supporting">
            Showing {drawnLimit} activities on this record, in the order you chose.
          </p>
          {drawnLimit < TIMELINE_CEILING
            /* The SHARED Button, not a raw one: the adoption ratchet in
               packages/ui counts raw buttons per host and it caught this,
               which is the ratchet doing its job rather than a number to
               bump. It also gets the loading treatment for free. */
            ? <Button size="sm" variant="secondary" data-testid="tl-load-more"
                loading={contextual.isFetching}
                onClick={() => setPages((value) => value + 1)}>
                Show {TIMELINE_PAGE} more
              </Button>
            /* The door will not go past its ceiling, so at that point asking
               again is not an option the product has — saying so is better
               than a button that quietly stops working. */
            : <p>That is as far as this view reaches. Narrow the search or the
                filters to bring older activities into it.</p>}
        </div>
      )}
    </aside>
  )
}

/* ── tenant #3: ownership & access — WHO can touch this record. Owner (RLS
 *    floor) + team + the record's shares (core.access_grant, 0006), and the
 *    4b actions: SHARE (grant) · REVOKE · owner REASSIGN — each lands in the
 *    record's history the Timeline's ⛭ interleave reads (share/revoke as a
 *    recorded act; a reassignment as the server's own change capture). ── */
function Access({ entity, id }: { entity: string; id: string }) {
  const { client } = useSolum()
  const qc = useQueryClient()
  const row = useEntityRow(entity, id)
  const saveRec = useSaveEntity(entity)
  const ownerName = useOwners()
  const teams = useRelated('core', 'team', {})
  const users = useRelated('core', 'app_user', { kind: 'human' })
  const grants = useRelated('core', 'access_grant', { entity_type: entity, entity_id: id })
  const [sharing, setSharing] = useState(false)
  const [reassigning, setReassigning] = useState(false)
  const rec = row.data as Row | null
  const teamName = (tid: unknown) => String(((teams.data ?? []) as Row[]).find((t) => t.id === tid)?.name ?? '—')
  const who = (kind: unknown, gid: unknown) => kind === 'team' ? `${teamName(gid)} (team)` : ownerName(gid)
  const list = (grants.data ?? []) as Row[]
  const humans = ((users.data ?? []) as Row[]).filter((u) => u.auth_user_id)

  const today = () => new Date().toISOString().slice(0, 10)
  const audit = (action: string, detail: string) =>
    client.raw().rpcScalar('audit', {
      p_action: action,
      p_subject_type: entity,
      p_subject_id: id,
      p_payload: { detail },
    }, 'core')
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['related', 'core', 'access_grant', { entity_type: entity, entity_id: id }] })
    void qc.invalidateQueries({ queryKey: recordHistoryKey(entity, id) })
  }
  const share = async (kind: 'user' | 'team', gid: string, mode: 'read' | 'write') => {
    await client.raw().insert('core', 'access_grant', {
      entity_type: entity, entity_id: id, grantee_kind: kind, grantee_id: gid, mode, granted_by: me(), granted_at: today(),
    })
    await audit('access.share.granted', `${who(kind, gid)} · ${mode}`)
    refresh(); setSharing(false)
  }
  const revoke = async (g: Row) => {
    await client.raw().delete('core', 'access_grant', String(g.id))
    await audit('access.share.revoked', `${who(g.grantee_kind, g.grantee_id)} · ${String(g.mode)}`)
    refresh()
  }
  /* The reassignment writes no audit row of its own (#2444 Q2): the server's
     change capture records `record.updated` with the owner pair, and the
     history derives "A → B" from THAT, naming each person only where the
     reader may see them. A browser-written copy was a second, unchecked
     account of the same fact. */
  const reassign = async (principal: string) => {
    await saveRec.mutateAsync({ id, patch: { owner_id: principal } })
    refresh(); setReassigning(false)
  }

  return (
    <div className={s.insights} data-testid="access">
      <div className={s.tlhead} style={{ padding: 0 }}><span className={s.tlT}>Ownership &amp; access</span></div>
      <FactList framed facts={[{
        key: 'owner',
        label: 'Owner',
        value: (<>
          ♛ {rec?.owner_id ? ownerName(rec.owner_id) : '—'}
          <button className={s.accessAct} data-testid="access-reassign" title="Reassign the owner (audited)"
            onClick={() => setReassigning((v) => !v)}>⇄</button>
        </>),
      }]} />
      {reassigning && (
        <div className={s.accessForm} data-testid="access-reassign-form">
          {humans.filter((u) => String(u.auth_user_id) !== String(rec?.owner_id)).map((u) => (
            <button key={String(u.id)} className={s.accessPick}
              onClick={() => void reassign(String(u.auth_user_id))}>♛ {ownerName(u.auth_user_id)}</button>
          ))}
        </div>
      )}
      <FactList framed facts={[
        { key: 'team', label: 'Team', value: rec?.team_id ? teamName(rec.team_id) : '—' },
      ]} />
      <div>
        <div className={s.sectionLabel} style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
          Shared with
          <button className={s.accessAct} data-testid="access-share" title="Share this record (audited)"
            onClick={() => setSharing((v) => !v)}>＋</button>
        </div>
        {list.map((g) => (
          <div key={String(g.id)} className={s.accessRow}>
            <span className={s.accessWho}>{who(g.grantee_kind, g.grantee_id)}</span>
            <Pill tone={g.mode === 'write' ? 'brand' : 'neutral'}>{String(g.mode)}</Pill>
            <span className={s.accessBy}>by {ownerName(g.granted_by)} · {fmtDate(g.granted_at)}</span>
            <button className={s.accessAct} data-testid="access-revoke" title="Revoke this share (audited)"
              onClick={() => void revoke(g)}>✕</button>
          </div>
        ))}
        {list.length === 0 && <EmptyState>No record-level shares — visibility follows the entity's scope.</EmptyState>}
        {sharing && (
          <div className={s.accessForm} data-testid="access-share-form">
            {humans.map((u) => (
              <div key={String(u.id)} className={s.accessPickRow}>
                <span className={s.accessWho}>{ownerName(u.auth_user_id)}</span>
                <button className={s.accessPick} onClick={() => void share('user', String(u.auth_user_id), 'read')}>read</button>
                <button className={s.accessPick} onClick={() => void share('user', String(u.auth_user_id), 'write')}>write</button>
              </div>
            ))}
            {((teams.data ?? []) as Row[]).map((t) => (
              <div key={String(t.id)} className={s.accessPickRow}>
                <span className={s.accessWho}>{String(t.name)} (team)</span>
                <button className={s.accessPick} onClick={() => void share('team', String(t.id), 'read')}>read</button>
                <button className={s.accessPick} onClick={() => void share('team', String(t.id), 'write')}>write</button>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="supporting">Owner + team set the RLS floor (0005); shares widen it per record (0006). Every change here is audited — in production these actions run share-authority-checked (0006's trigger) and staff-gated.</div>
    </div>
  )
}

/* ── tenant #2: quick insights — the record's numbers, or MY / the team's book ── */
/**
 * TEC-02: the book is filtered and counted BY THE SERVER.
 *
 * These three figures were `useEntityRows('crm_opportunity')` — a 500-row page —
 * filtered in the browser by account or owner. Two failures compounded: on a
 * >500-row deployment the totals were simply wrong, and the record-scoped view
 * was worse than wrong, because filtering a 500-row page by `account_id` shows
 * an account's deals only if that account happened to be in the window at all.
 * `useEntityPage` pushes the scope filter down and returns the true total; the
 * money sum is still over the loaded page and SAYS SO when the two differ.
 */
function Insights({ entity, id, scope }: { entity: string; id: string; scope: DrawerScope }) {
  const isOrg = entity === 'core_organization'
  // Per-entity cards, looked up rather than branched on inline (#337). The CRM
  // figures below are the lens's general case and stay where they are; this is
  // the seam a module uses to say what "insight" means for ITS record, so the
  // next one does not add a second `if (entity === …)`.
  const Card = RECORD_INSIGHT[entity]
  const scopeFilter = scope === 'record'
    ? (isOrg ? [{ key: 'account_id', value: `eq.${id}` }] : null)
    : scope === 'mine' ? [{ key: 'owner_id', value: `eq.${me()}` }] : []
  const bookQ = useEntityPage('crm_opportunity', {
    // `stage=not.in.(won,lost)` is the same predicate the JS filter applied,
    // translated faithfully rather than approximated.
    filters: [...(scopeFilter ?? []), { key: 'stage', value: 'not.in.(won,lost)' }],
    // A person record has no book — do not fetch one to then render nothing.
    enabled: scopeFilter != null,
  })
  const wonQ = useEntityCount('crm_opportunity',
    [...(scopeFilter ?? []), { key: 'stage', value: 'eq.won' }], scopeFilter != null)
  const edges = useRelated('core', 'relationship', { rel_type: 'works_at', to_type: 'core_organization', to_id: id }, scope === 'record' && isOrg)
  const acts = useRelated('core', 'relationship', { rel_type: 'activity_about', to_type: entity, to_id: id }, scope === 'record')

  const open = bookQ.data.rows
  const openTotal = bookQ.data.total
  const partial = openTotal != null && openTotal > open.length
  const title = scope === 'mine' ? 'My book — insights' : scope === 'team' ? 'Team book — insights' : 'Insights'
  return (
    <div className={s.insights} data-testid="insights">
      <div className={s.tlhead} style={{ padding: 0 }}><span className={s.tlT}>{title}</span></div>
      {scope === 'record' && Card && <Card id={id} />}
      {/* Five facts behind three conditions, previously five copies of the same
          label/value markup with the condition wrapped around each. Which facts
          apply is still the host's decision; drawing them is not. */}
      <FactList framed facts={[
        ...(scope !== 'record' || isOrg ? [
          /* A book that could not be read is ABSENT, never €0 / 0 (#2444: an
             opportunity read through a governed door that cannot apply this
             lens's `not in (won, lost)` is refused, and a refusal is not an
             empty book). */
          { key: 'pipeline',
            label: `Open pipeline${partial ? ` (first ${open.length} of ${openTotal})` : ''}`,
            value: <span className="tnum">{bookQ.isError ? '—' : fmtMoney(open.reduce((a, o) => a + Number(o.amount ?? 0), 0))}</span> },
          { key: 'open', label: 'Open opportunities', value: <span className="tnum">{bookQ.isError ? '—' : openTotal ?? open.length}</span> },
          { key: 'won', label: 'Won', value: <span className="tnum">{wonQ.data ?? '—'}</span> },
        ] : []),
        ...(scope === 'record' && isOrg
          ? [{ key: 'contacts', label: 'Contacts', value: <span className="tnum">{((edges.data ?? []) as Row[]).length}</span> }]
          : []),
        ...(scope === 'record'
          ? [{ key: 'activities', label: 'Activities', value: <span className="tnum">{((acts.data ?? []) as Row[]).length}</span> }]
          : []),
      ]} />
      <div className="supporting">Live from the graph — dashboards dock here next.</div>
    </div>
  )
}
